// Modal for creating and editing Outbound HTTP API endpoints (WP-13).
import { useState, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import {
  HTTP_METHODS,
  saveHttpApi,
  type HttpMethod,
  type HttpApiDoc,
  type ResponseMapping,
} from '../../lib/httpApi';

interface HttpApiModalProps {
  open: boolean;
  onClose: () => void;
  api: HttpApiDoc | null;
  onSaved: () => void;
}

interface HeaderRow {
  key: string;
  value: string;
  isSecret: boolean;
}

export function HttpApiModal({ open, onClose, api, onSaved }: HttpApiModalProps) {
  const { t } = useI18n();

  const [name, setName] = useState('');
  const [method, setMethod] = useState<HttpMethod>('GET');
  const [url, setUrl] = useState('');
  const [headers, setHeaders] = useState<HeaderRow[]>([]);
  const [bodyTemplate, setBodyTemplate] = useState('');
  const [mappings, setMappings] = useState<ResponseMapping[]>([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (api) {
      setName(api.name);
      setMethod(api.method);
      setUrl(api.url);
      setBodyTemplate(api.bodyTemplate || '');

      const secretSet = new Set((api.secretHeaders || []).map((s) => s.toLowerCase()));
      const headerRows: HeaderRow[] = Object.entries(api.headers || {}).map(([k, v]) => ({
        key: k,
        value: v,
        isSecret: secretSet.has(k.toLowerCase()),
      }));
      setHeaders(headerRows);
      setMappings(api.responseMapping || []);
    } else {
      setName('');
      setMethod('GET');
      setUrl('');
      setHeaders([{ key: 'Content-Type', value: 'application/json', isSecret: false }]);
      setBodyTemplate('');
      setMappings([]);
    }
    setError(null);
  }, [open, api]);

  const addHeaderRow = () => {
    setHeaders([...headers, { key: '', value: '', isSecret: false }]);
  };

  const removeHeaderRow = (index: number) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const updateHeaderRow = (index: number, field: keyof HeaderRow, val: any) => {
    const updated = [...headers];
    updated[index] = { ...updated[index], [field]: val };
    setHeaders(updated);
  };

  const addMappingRow = () => {
    setMappings([...mappings, { path: '', variable: '' }]);
  };

  const removeMappingRow = (index: number) => {
    setMappings(mappings.filter((_, i) => i !== index));
  };

  const updateMappingRow = (index: number, field: keyof ResponseMapping, val: string) => {
    const updated = [...mappings];
    updated[index] = { ...updated[index], [field]: val };
    setMappings(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !url.trim()) {
      setError(t('httpApi.modal.nameAndUrlRequired'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const headersRecord: Record<string, string> = {};
      const secretHeaders: string[] = [];

      for (const h of headers) {
        const k = h.key.trim();
        if (!k) continue;
        headersRecord[k] = h.value;
        if (h.isSecret) {
          secretHeaders.push(k);
        }
      }

      const validMappings = mappings
        .map((m) => ({ path: m.path.trim(), variable: m.variable.trim() }))
        .filter((m) => m.path && m.variable);

      await saveHttpApi({
        id: api?.id,
        name: name.trim(),
        method,
        url: url.trim(),
        headers: headersRecord,
        secretHeaders,
        bodyTemplate: ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) ? bodyTemplate : '',
        responseMapping: validMappings,
      });

      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || t('httpApi.modal.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={api ? t('httpApi.modal.editTitle') : t('httpApi.modal.createTitle')}
    >
      <form onSubmit={handleSubmit} className="space-y-4 max-h-[80vh] overflow-y-auto pe-1">
        {error && (
          <div className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
            {error}
          </div>
        )}

        {/* Name */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1">
            {t('httpApi.modal.endpointName')} *
          </label>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('httpApi.modal.namePlaceholder')}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900"
          />
        </div>

        {/* Method & URL */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <div className="sm:col-span-1">
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1">
              {t('httpApi.modal.method')} *
            </label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value as HttpMethod)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900"
            >
              {HTTP_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-3">
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1">
              {t('httpApi.modal.url')} *
            </label>
            <input
              type="text"
              required
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://api.example.com/v1/users?phone={{phone}}"
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900"
            />
          </div>
        </div>
        <p className="text-xs text-slate-500">
          {t('httpApi.modal.urlHint')} <code className="text-primary font-mono">{'{{name}}'}</code>,{' '}
          <code className="text-primary font-mono">{'{{phone}}'}</code>,{' '}
          <code className="text-primary font-mono">{'{{custom:key}}'}</code>,{' '}
          <code className="text-primary font-mono">{'{{var:key}}'}</code>
        </p>

        {/* Headers Section */}
        <div className="border-t border-slate-200 pt-3 dark:border-slate-800">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
              {t('httpApi.modal.headers')}
            </label>
            <button
              type="button"
              onClick={addHeaderRow}
              className="text-xs font-medium text-primary hover:underline"
            >
              + {t('httpApi.modal.addHeader')}
            </button>
          </div>

          {headers.length === 0 ? (
            <p className="text-xs text-slate-400 italic">{t('httpApi.modal.noHeaders')}</p>
          ) : (
            <div className="space-y-2">
              {headers.map((h, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={h.key}
                    onChange={(e) => updateHeaderRow(idx, 'key', e.target.value)}
                    placeholder={t('httpApi.modal.headerKeyPlaceholder')}
                    className="w-1/3 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900 font-mono"
                  />
                  <input
                    type={h.isSecret && h.value === '••••••••' ? 'text' : h.isSecret ? 'password' : 'text'}
                    value={h.value}
                    onChange={(e) => updateHeaderRow(idx, 'value', e.target.value)}
                    placeholder={t('httpApi.modal.headerValuePlaceholder')}
                    className="flex-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900 font-mono"
                  />
                  <label className="flex items-center gap-1 text-[11px] text-slate-500 whitespace-nowrap cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={h.isSecret}
                      onChange={(e) => updateHeaderRow(idx, 'isSecret', e.target.checked)}
                      className="rounded border-slate-300 text-primary focus:ring-primary"
                    />
                    <span>{t('httpApi.modal.secret')}</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => removeHeaderRow(idx)}
                    className="text-slate-400 hover:text-rose-500 text-base px-1"
                    title={t('common.delete')}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Body Template (for non-GET) */}
        {['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && (
          <div className="border-t border-slate-200 pt-3 dark:border-slate-800">
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1">
              {t('httpApi.modal.bodyTemplate')}
            </label>
            <textarea
              rows={4}
              value={bodyTemplate}
              onChange={(e) => setBodyTemplate(e.target.value)}
              placeholder={'{\n  "phone": "{{phone}}",\n  "status": "active"\n}'}
              className="w-full font-mono text-xs rounded-lg border border-slate-200 bg-slate-50 p-2.5 outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900"
            />
            <p className="text-[11px] text-slate-400 mt-1">{t('httpApi.modal.bodyTemplateHint')}</p>
          </div>
        )}

        {/* Response Mapping */}
        <div className="border-t border-slate-200 pt-3 dark:border-slate-800">
          <div className="flex items-center justify-between mb-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 block">
                {t('httpApi.modal.responseMapping')}
              </label>
              <p className="text-[11px] text-slate-400">{t('httpApi.modal.responseMappingDesc')}</p>
            </div>
            <button
              type="button"
              onClick={addMappingRow}
              className="text-xs font-medium text-primary hover:underline"
            >
              + {t('httpApi.modal.addMapping')}
            </button>
          </div>

          {mappings.length === 0 ? (
            <p className="text-xs text-slate-400 italic">{t('httpApi.modal.noMappings')}</p>
          ) : (
            <div className="space-y-2">
              {mappings.map((m, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={m.path}
                    onChange={(e) => updateMappingRow(idx, 'path', e.target.value)}
                    placeholder="data.user.id"
                    className="flex-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900 font-mono"
                  />
                  <span className="text-slate-400 text-xs">→</span>
                  <input
                    type="text"
                    value={m.variable}
                    onChange={(e) => updateMappingRow(idx, 'variable', e.target.value)}
                    placeholder="crm_user_id"
                    className="flex-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900 font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => removeMappingRow(idx)}
                    className="text-slate-400 hover:text-rose-500 text-base px-1"
                    title={t('common.delete')}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal Actions */}
        <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-800">
          <Button variant="ghost" type="button" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
